import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bookmark, BookOpen, Clock, Eye, Heart, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const ReadingListPage = () => {
  const { api } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const res = await api.get('/bookmarks');
        setStories(res.data);
      } catch (e) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };
    fetchBookmarks();
  }, [api]);

  const removeBookmark = async (storyId) => {
    try {
      await api.post(`/bookmarks/${storyId}`);
      setStories(stories.filter(s => s.id !== storyId));
    } catch (e) {
      // Non-critical error suppressed
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="reading-list-page">
      <div className="flex items-center gap-3 mb-8">
        <Bookmark className="w-7 h-7 text-brand-accent" />
        <h1 className="font-serif text-3xl font-semibold text-text-primary">My Reading List</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-16">
          <Bookmark className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-xl text-text-primary mb-2">Your reading list is empty</h2>
          <p className="text-text-secondary mb-6">Bookmark stories you want to read later</p>
          <Link to="/stories">
            <Button className="btn-accent gap-2">
              <BookOpen className="w-4 h-4" />
              Browse Stories
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="bookmark-list">
          {stories.map(story => (
            <Card key={story.id} className="border-[#E5E2DC] hover:border-brand-primary/30 transition-colors" data-testid={`bookmark-${story.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <Link to={`/story/${story.id}`} className="group">
                      <h3 className="font-serif text-lg font-medium text-text-primary group-hover:text-brand-primary transition-colors">
                        {story.title}
                      </h3>
                    </Link>
                    <p className="text-sm text-text-muted mt-1">
                      by {story.author_name} &middot; {story.genre}
                    </p>
                    {story.summary && (
                      <p className="text-sm text-text-secondary mt-2 line-clamp-2">{story.summary}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-text-muted">
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{story.view_count || 0}</span>
                      <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{story.like_count || 0}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Saved {new Date(story.bookmarked_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBookmark(story.id)}
                    className="text-brand-accent hover:text-red-500 flex-shrink-0"
                    data-testid={`remove-bookmark-${story.id}`}
                  >
                    <Bookmark className="w-4 h-4 fill-current" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReadingListPage;
